import { useEffect, useState } from "react";
import type { GolfFitAssessment } from "@/services/schoolFits";
import {
  type BrandedSchool,
  formatDivision,
  schoolBrandStyle,
  schoolInitials,
  schoolLocation,
} from "./schoolBrand";
import { TierDot } from "./TierDot";

type HeaderVariant = "compact" | "card" | "detail";

interface SchoolBrandHeaderProps {
  school: BrandedSchool;
  fit?: GolfFitAssessment | null;
  variant?: HeaderVariant;
}

function formatFitDate(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(date);
}

function fitLabel(fit?: GolfFitAssessment | null): string {
  if (fit?.status === "unavailable") return "Needs data";
  if (fit?.tier) return fit.tier.charAt(0).toUpperCase() + fit.tier.slice(1);
  if (fit?.status === "updating") return "Assessing";
  return "Not assessed";
}

function SchoolLogo({ school, variant }: { school: BrandedSchool; variant: HeaderVariant }) {
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => setImageFailed(false), [school.logoUrl]);
  const showImage = !!school.logoUrl && !imageFailed;
  const size = variant === "compact" ? "h-12 w-12 rounded-[13px]" : "h-14 w-14 rounded-[15px]";

  return (
    <div
      className={`grid ${size} shrink-0 place-items-center overflow-hidden border border-white/65 bg-white/95 p-1.5 text-[var(--school-primary)] shadow-[0_9px_22px_rgba(0,0,0,0.13)]`}
    >
      {showImage && (
        <img
          src={school.logoUrl!}
          alt={`${school.schoolName} logo`}
          className="h-full w-full object-contain"
          onError={() => setImageFailed(true)}
        />
      )}
      {!showImage && (
        <span className="font-display text-base font-extrabold tracking-[-0.04em]">
          {schoolInitials(school.schoolName)}
        </span>
      )}
    </div>
  );
}

function FitPlaque({ fit, compact }: { fit?: GolfFitAssessment | null; compact: boolean }) {
  const score = fit?.status === "unavailable" ? null : fit?.score;
  return (
    <div
      className={`shrink-0 rounded-[13px] border border-white/30 bg-black/20 text-center text-white backdrop-blur-sm [.pipeline-brand-light_&]:border-black/10 [.pipeline-brand-light_&]:bg-white/50 [.pipeline-brand-light_&]:text-forest ${compact ? "min-w-[60px] px-2 py-2" : "min-w-[70px] px-2.5 py-2.5"}`}
    >
      <strong
        className={`block font-mono font-extrabold leading-none tracking-[-0.06em] ${compact ? "text-lg" : "text-xl"}`}
      >
        {score ?? "—"}
        {score != null && <small className="ml-0.5 text-[11px] opacity-70">/100</small>}
      </strong>
      <span className="mt-1 block text-[11px] font-bold">Golf fit</span>
    </div>
  );
}

export function SchoolBrandHeader({ school, fit, variant = "card" }: SchoolBrandHeaderProps) {
  const style = schoolBrandStyle(school);
  const isLight = style["--school-ink"] === "#17231D";
  const compact = variant === "compact";
  const detail = variant === "detail";
  const location = schoolLocation(school);
  const date = formatFitDate(fit?.assessedAt);
  const score = fit?.status === "unavailable" ? null : fit?.score;

  return (
    <div
      style={style}
      className={`pipeline-brand relative overflow-hidden bg-[radial-gradient(circle_at_84%_0%,rgba(255,255,255,0.1),transparent_31%),linear-gradient(135deg,var(--school-primary),var(--school-secondary))] text-[var(--school-ink)] ${isLight ? "pipeline-brand-light" : ""} ${compact ? "min-h-[112px] p-4" : detail ? "px-5 pb-5 pt-6 sm:px-6" : "min-h-[132px] p-[18px]"}`}
    >
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute right-0 font-display font-extrabold leading-none opacity-[0.065] ${compact ? "-bottom-8 text-[105px]" : "-bottom-12 text-[140px]"}`}
      >
        {schoolInitials(school.schoolName).slice(0, 1)}
      </span>

      <div className="relative z-10 flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <SchoolLogo school={school} variant={variant} />
          <div className="min-w-0">
            <h2
              className={`line-clamp-2 font-display font-extrabold leading-[1.08] tracking-[-0.035em] ${detail ? "text-2xl" : compact ? "text-[17px]" : "text-[20px]"}`}
            >
              {school.schoolName}
            </h2>
            <p className="mt-1.5 text-[11px] font-medium leading-snug opacity-90">
              {[formatDivision(school.division), location].filter(Boolean).join(" · ")}
            </p>
          </div>
        </div>
        {!detail && <FitPlaque fit={fit} compact={compact} />}
      </div>

      {detail ? (
        <div className="relative z-10 mt-6 flex items-end justify-between gap-4">
          <div>
            <strong className="font-mono text-[34px] font-extrabold leading-none tracking-[-0.06em]">
              {score ?? "—"}
              {score != null && <small className="ml-0.5 text-xs opacity-70">/100</small>}
            </strong>
            <span className="mt-2 block text-xs font-bold">Golf fit</span>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-[11px] font-extrabold text-forest shadow-sm">
            <TierDot tier={fit?.status === "unavailable" ? null : fit?.tier} />
            {fitLabel(fit)}
          </span>
        </div>
      ) : (
        <div className="relative z-10 mt-3.5 flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-[11px] font-extrabold text-forest shadow-sm">
            <TierDot tier={fit?.status === "unavailable" ? null : fit?.tier} />
            {fitLabel(fit)}
          </span>
          <span className="text-[11px] font-bold opacity-90">
            {fit?.status === "updating"
              ? "Updating with new results"
              : fit?.status === "stale"
                ? "New golf data available"
                : date
                  ? `Updated ${date}`
                  : "Not scored yet"}
          </span>
        </div>
      )}
    </div>
  );
}
